import DOMPurify from 'dompurify';

/**
 * `dangerouslySetInnerHTML` に渡す HTML のサニタイズ。
 *
 * 対象は「サーバー由来の HTML をそのまま描画している箇所」:
 *   - NewsDetailPage / NewsPopupModal / NewsManagementSection のプレビュー
 *     （管理画面の RichTextEditor が生成した本文 HTML）
 *   - DocumentCreationTest（`/api/test/generate-html` が返すスライド HTML。
 *     このエンドポイントは未認証で任意の designSpec を受け取り、Gemini が
 *     組み立てた完全な HTML ドキュメントを返すため、無検査で描画すると
 *     HTML 注入がそのまま通る）→ こちらは `sanitizeDocumentHtml` を使う。
 *
 * ## 許可リストの根拠
 *
 * RichTextEditor（`components/admin/RichTextEditor.tsx`）は contentEditable +
 * `document.execCommand` 方式なので、出力タグはツールバー機能だけでは決まらない。
 *   - ツールバー由来: `b`/`strong`（bold）、`h2`（formatBlock）、`ul`/`li`
 *     （insertUnorderedList）、`a[href][target][rel][style]`（createLink /
 *     insertHTML）、`img[src][alt][style]`（画像挿入）
 *   - Enter 由来: ブラウザ実装により `div` / `p` / `br`
 *   - 貼り付け由来: contentEditable は書式付きペーストを受けるため、見出し
 *     （h1-h6）・番号付きリスト・表・引用・整形済みテキスト・インライン装飾が
 *     混入する。NewsDetailPage 側の表示 CSS が `table` / `pre` / `blockquote` /
 *     `h1`-`h3` / `ol` まで面倒を見ている（NewsDetailPage.tsx:116-128）ことが、
 *     これらが実際に保存され得る証拠。
 * 以上をカバーする範囲でタグを許可し、それ以外は落とす。
 *
 * `style` 属性は RichTextEditor が挿入 HTML に直接埋め込んでいる
 * （RichTextEditor.tsx:64, 82）ため必須。既存の公開済みニュースの見た目を
 * 壊さないために維持する。`class` と `style` 要素も、インライン `style` を
 * 許可した時点で「任意の CSS を書ける」という能力は同じなので許可する（CSS は
 * 現行ブラウザでスクリプトを実行できない）。`<style>` 要素だけはページ全体の
 * CSS を書き換えられるため、スライド HTML 用の `sanitizeDocumentHtml` に限定する
 * （下の DOCUMENT_ONLY_TAGS）。`<style>` 経由の外部リクエストは通したくないので、
 * `@import` 規則は下のフックで除去する。
 *
 * `id` / `name` は許可しない。untrusted な HTML から任意の id を持ち込めると
 * NewsPopupModal の `aria-labelledby`（`id="news-popup-title"`）を横取りできる
 * うえ、DOM clobbering の足場になるため。`data-*` も同じ理由で無効化する。
 *
 * ## iframe を許可しない理由
 *
 * RichTextEditor に埋め込み用の UI は無く（ツールバーは太字・見出し・箇条書き・
 * リンク・画像の 5 つだけ）、YouTube 等の iframe を生成する経路が存在しない。
 * 許可しても得られるものが無い一方で注入面が広がるので `iframe` は落とす。
 * 将来的に埋め込み機能を足す場合は、ここで `src` のホストを
 * `https://www.youtube.com/embed/` などに限定するフックを追加すること。
 */
const ALLOWED_TAGS = [
  // ブロック
  'p',
  'div',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'li',
  'figure',
  'figcaption',
  // 表
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'colgroup',
  'col',
  // インライン
  'span',
  'a',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'code',
  'small',
  'sub',
  'sup',
  'mark',
  // メディア
  'img',
];

/**
 * `<style>` 要素はスライド HTML（sanitizeDocumentHtml）だけに許可する。
 * ニュース本文で許可すると、管理者由来とはいえ全ユーザーのページ全体の CSS
 * （`body{display:none}` や偽のオーバーレイ等）を書き換えられる。RichTextEditor が
 * `<style>` 要素を生成する経路は無いので、こちらを落としても表示は変わらない。
 * DOMPurify 既定の FORBID_CONTENTS に style が含まれるため、中身の CSS 文字列が
 * 本文テキストとして漏れることもない。
 */
const DOCUMENT_ONLY_TAGS = ['style'];

const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'title',
  'width',
  'height',
  'style',
  'class',
  'colspan',
  'rowspan',
  'align',
  'start',
  'type',
  'lang',
  'dir',
];

/** `<style>` 内の `@import` 規則（外部 CSS の読み込み）を落とす。 */
const CSS_IMPORT_REGEX = /@import\b[^;]*;?/gi;

/** Node.ELEMENT_NODE。`instanceof Element` は realm 依存で別 realm のノードに
 *  対して false になり得るため、nodeType で判定する（DOMPurify 自身も同じ理由で
 *  instanceof を避けている）。 */
const ELEMENT_NODE = 1;

// フックは DOMPurify インスタンスに対してグローバルに積まれる。sanitize の
// 呼び出しごとに addHook すると多重登録になるため、モジュール評価時に一度だけ
// 登録する。
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // このフックの引数は Element 型（ElementHook）。
  if (node.nodeType !== ELEMENT_NODE || node.tagName !== 'A') return;

  const target = node.getAttribute('target');
  // 別ウィンドウを開くリンクには reverse tabnabbing 対策を強制する。
  // `_blank` だけでなく名前付きターゲットも対象にし、HTML 側が指定してきた
  // rel（`rel="opener"` など）は必ず上書きする。
  if (target && target !== '_self') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

DOMPurify.addHook('afterSanitizeElements', (node) => {
  // このフックの引数は Node 型（NodeHook）なので要素に絞り込む。
  if (node.nodeType !== ELEMENT_NODE) return;
  const element = node as Element;
  if (element.tagName !== 'STYLE') return;

  const css = element.textContent ?? '';
  // `test()` は /g 付き正規表現の lastIndex を進めてしまうので使わず、
  // 常に replace を通して差分で判定する。
  const stripped = css.replace(CSS_IMPORT_REGEX, '');
  if (stripped !== css) {
    element.textContent = stripped;
  }
});

const BASE_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  // 相対 URL や mailto は許すが javascript: / vbscript: は落とす
  // （DOMPurify 既定の ALLOWED_URI_REGEXP に従う）。
  KEEP_CONTENT: true,
} as const;

/**
 * HTML 断片を `dangerouslySetInnerHTML` に渡せる形に浄化する。
 *
 * スクリプト（`<script>` / `on*` ハンドラ / `javascript:` URL）と、許可リストに
 * 無いタグ・属性を除去した HTML 文字列を返す。
 *
 * 注意: DOMPurify は body だけをシリアライズするため、文字列の先頭に置かれた
 * `<style>` は HTML パーサに head へ持ち上げられて結果から消える。完全な HTML
 * ドキュメント（DOCTYPE や head を含む）を扱う場合は `sanitizeDocumentHtml`
 * を使うこと。
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html, BASE_CONFIG);
};

/**
 * 完全な HTML ドキュメント（DOCTYPE / html / head / body を含む）を浄化する。
 *
 * `sanitizeHtml` と同じ許可リストを使うが、`WHOLE_DOCUMENT` を有効にして head を
 * 残す。`/api/test/generate-html` が返すスライド HTML は CSS を head の
 * `<style>` に置くため、これが無いと配色・レイアウトが全部落ちる。
 *
 * 返り値には `<html>` / `<head>` / `<body>` が含まれるが、これを div の
 * innerHTML として流し込むとパーサがこの 3 つの開始タグを無視し、中身
 * （`<style>` と本文）だけがその場に展開されるので描画結果は変わらない。
 */
export const sanitizeDocumentHtml = (html: string): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ...BASE_CONFIG,
    ALLOWED_TAGS: [...ALLOWED_TAGS, ...DOCUMENT_ONLY_TAGS],
    WHOLE_DOCUMENT: true,
  });
};

export default sanitizeHtml;
